import './styles/IOSettings.scss';

import { useQuery } from '@apollo/client';
import { Button, Grid, Paper } from '@mui/material';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import QUERY from 'gql/queries/InternationalOffice/getIOSettings.gql';

import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { LabeledInput } from 'app/components/RichList/items/CompanyItem/components/LabeledInput';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { IOSettingsModal } from './IOSettingsModal';

export function IOSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const settingsQuery = useQuery<any, any>(QUERY);
  usePageHeading({
    heading: t(translations.layout.menu.settingsInternationalOffice()),
    mobilePadding: false
  });

  return (
    <GQLGuard
      query={settingsQuery}
      onError={error => {
        dispatch(
          AlertActions.error(
            `${t(translations.pages.internationalOffice.events.alerts.error.loadingEvents())}`
          )
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {settingsData => {
        return (
          <>
            <ErrorBoundary boundaryTag="ioCourseMangegementBoundary">
              <Modal
                width="small"
                open={settingsModalOpen}
                onClose={(event, reason) => {
                  if (reason && reason === 'backdropClick') return;
                  setSettingsModalOpen(false);
                }}
                headline={t(translations.layout.menu.settingsInternationalOffice())}
                onCancel={() => setSettingsModalOpen(false)}
                disabledCancel={disabledCancel}
              >
                <IOSettingsModal
                  closeHandler={() => {
                    setSettingsModalOpen(false);
                  }}
                  setDisabledCancel={setDisabledCancel}
                  settings={settingsData.ioSettings}
                />
              </Modal>
            </ErrorBoundary>
            <Grid className="ioSettings" container>
              <Grid className="ioSettings__container" container component={Paper} elevation={3}>
                <Grid className="ioSettings__container__headline" item xs={12} style={{}}>
                  {t(translations.layout.menu.settingsInternationalOffice())}
                </Grid>
                <Grid item xs={12} md={4}>
                  <LabeledInput
                    style={{ fontSize: '12pt' }}
                    label={t(translations.pages.ioSettings.year())}
                  >
                    <Grid className="ioSettings__container__field" container>
                      {settingsData.ioSettings.year ? settingsData.ioSettings.year : '---'}
                    </Grid>
                  </LabeledInput>
                </Grid>
                <Grid item xs={12} md={4}>
                  <LabeledInput
                    style={{ fontSize: '12pt' }}
                    label={t(translations.pages.ioSettings.semesterType())}
                  >
                    <Grid className="ioSettings__container__field" container>
                      {settingsData.ioSettings.semesterType
                        ? settingsData.ioSettings.semesterType
                        : '---'}
                    </Grid>
                  </LabeledInput>
                </Grid>
                <Grid item xs={12} md={4}>
                  <LabeledInput
                    style={{ fontSize: '12pt' }}
                    label={t(translations.pages.ioSettings.examRegistration())}
                  >
                    <Grid className="ioSettings__container__field" container>
                      {settingsData.ioSettings.examRegistration
                        ? new Date(settingsData.ioSettings.examRegistration).toLocaleDateString()
                        : '---'}
                    </Grid>
                  </LabeledInput>
                </Grid>
                <Grid className="ioSettings__container__button" item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setSettingsModalOpen(true)}
                  >
                    {t(translations.pages.ioSettings.edit())}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </>
        );
      }}
    </GQLGuard>
  );
}
